import { useEffect, useState } from 'react';
import { FileSpreadsheet } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { api, throwIfNotOk } from '../../lib/api';
import { LoadingShell } from '@siliconx/shared/ui/state';
import { ERP_SOURCE_SYSTEMS_UI, ErpBrandIcon } from './ErpBrandIcon';
import { ErpImportWizard } from './ErpImportWizard';
import { Button } from '@siliconx/shared/ui/Button';

async function fetchTemplates() {
  const res = await api.api.client.erp.templates.$get();
  await throwIfNotOk(res);
  const { data } = await res.json();
  return data;
}

async function fetchCurrentBatch() {
  const res = await api.api.client['erp-register'].batches.current.$get();
  await throwIfNotOk(res);
  const { batch } = await res.json();
  return batch;
}

type CurrentBatch = Awaited<ReturnType<typeof fetchCurrentBatch>>;

/**
 * ERP fixed-asset register import card (SIL-270, W4-T10 of
 * resources/plans/erp-register-import-spec.md decision 7 — "wizard +
 * templates + triage live under Integrations (new ERP card; page charter is
 * inbound connections per RUF-378)").
 *
 * RBAC: org admin only for upload/mapping/promote (spec decision 7's same
 * gate as team management, mirrored from `user?.role === 'admin'` at the
 * page level, same posture as `DatadogCard`/`ServiceNowCard`). Members get a
 * read-only view — the saved template count, no upload controls (the
 * templates GET endpoint itself is any-member read-only, see
 * apps/api/src/routes/client/erp-templates.ts).
 *
 * Spans the full grid width while the wizard is open (mirrors
 * `DiscoveredAssetsCard`'s `md:col-span-2 lg:col-span-3` — the wizard needs
 * more horizontal room than a single card cell).
 */
export function ErpRegisterCard({ isAdmin }: { isAdmin: boolean }) {
  const [templates, setTemplates] = useState<Awaited<ReturnType<typeof fetchTemplates>> | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [currentBatch, setCurrentBatch] = useState<CurrentBatch | null>(null);
  const [abandoning, setAbandoning] = useState(false);
  const [abandonError, setAbandonError] = useState<string | null>(null);

  useEffect(() => {
    fetchTemplates()
      .then((data) => setTemplates(data))
      .catch(() => setTemplates([]));
  }, [wizardOpen]);

  // SIL-279: an in-flight batch (received/validating/validated) has no other
  // URL-addressable entry point once the wizard that created it is closed —
  // without this, a fully-matched-but-unpromoted batch was stranded (the
  // financial data never got written, and the one-running-import-per-org
  // guard blocked a fresh attempt). Re-fetched whenever the wizard closes,
  // since closing the wizard is the moment a batch could have just entered
  // (or left, on abandon/full-promote) the running set.
  useEffect(() => {
    fetchCurrentBatch()
      .then((batch) => setCurrentBatch(batch))
      .catch(() => setCurrentBatch(null));
  }, [wizardOpen]);

  // SIL-483: terminal action (batch -> abandoned) that frees the org's
  // single in-flight-import slot (erp_register_one_running_import_per_org,
  // 689) without requiring the batch to be triaged/promoted first. Endpoint
  // (SIL-278) already enforces admin-only server-side; confirm() here since
  // there's no undo.
  async function abandonImport() {
    if (!currentBatch) return;
    if (!confirm('Abandon this import? This cannot be undone and any un-promoted rows will be discarded.')) {
      return;
    }
    setAbandoning(true);
    setAbandonError(null);
    try {
      const res = await api.api.client['erp-register'].batches[':batchId'].abandon.$post({
        param: { batchId: currentBatch.id },
      });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      setCurrentBatch(null);
    } catch (ex: unknown) {
      setAbandonError((ex as Error).message);
    } finally {
      setAbandoning(false);
    }
  }

  return (
    <div
      className={
        wizardOpen
          ? 'space-y-3 rounded-lg border border-zinc-200 bg-white p-5 md:col-span-2 lg:col-span-3'
          : 'space-y-3 rounded-lg border border-zinc-200 bg-white p-5'
      }
    >
      <div className="flex items-center gap-2">
        <FileSpreadsheet className="size-5 text-zinc-500" />
        <h2 className="text-sm font-semibold text-zinc-900">ERP Fixed-Asset Register</h2>
      </div>
      <p className="text-xs text-zinc-500">
        Import your ERP's fixed-asset register (cost, depreciation, net book value) to fill in the financial data
        behind Book vs FMV and hold-vs-sell analysis.
      </p>

      <div className="flex items-center gap-1.5">
        {ERP_SOURCE_SYSTEMS_UI.map((s) => (
          <ErpBrandIcon key={s.id} system={s.id} size={20} />
        ))}
      </div>

      {templates === null ? (
        <LoadingShell rows={1} />
      ) : (
        <div className="text-xs text-zinc-400">
          {templates.length === 0
            ? 'No saved mapping templates yet.'
            : `${templates.length} saved mapping template${templates.length !== 1 ? 's' : ''}.`}
        </div>
      )}

      {!wizardOpen && isAdmin && currentBatch && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
          <p className="text-xs font-medium text-amber-800">Import in progress</p>
          <p className="mt-0.5 text-xs text-amber-700">
            {currentBatch.rowCounts.unapplied} row{currentBatch.rowCounts.unapplied === 1 ? '' : 's'} ready to promote
            {currentBatch.rowCounts.pending +
              currentBatch.rowCounts.ambiguous +
              currentBatch.rowCounts.lumpPartial +
              currentBatch.rowCounts.conflict >
              0 && ', more still need triage'}
            .
          </p>
          <div className="mt-1 flex items-center gap-3">
            <Link
              to="/financials/triage/$batchId"
              params={{ batchId: currentBatch.id }}
              className="inline-block text-xs font-medium text-brand-600 hover:underline"
            >
              Resume import
            </Link>
            <button
              type="button"
              onClick={abandonImport}
              disabled={abandoning}
              className="text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
            >
              {abandoning ? 'Abandoning…' : 'Abandon import'}
            </button>
          </div>
          {abandonError && <p className="mt-1 text-xs text-red-600">{abandonError}</p>}
        </div>
      )}

      {/* Starting a new import while one is running is rejected server-side
          (erp_register_one_running_import_per_org, 689 — 409
          import_already_running); hide the button in that state rather than
          let an admin hit that error, mirroring the Resume affordance above. */}
      {!wizardOpen &&
        !currentBatch &&
        (isAdmin ? (
          <Button size="sm"
            onClick={() => setWizardOpen(true)}
          >
            Start import
          </Button>
        ) : (
          <p className="text-xs text-zinc-400">Only an org admin can import an ERP register.</p>
        ))}

      {wizardOpen && <ErpImportWizard onClose={() => setWizardOpen(false)} />}
    </div>
  );
}
